import {
  addressFactory,
  isString,
  ParticipantDto,
  participantFactory,
  RegistrantDto,
  registrantFactory,
} from '@sportngin/se-commerce-sdk';
import {DateService} from '../../common/service/core';
import {Address, addressDtoToAddress} from './address';
import {CartComparator} from './cart-comparator';
import {FactoryFn} from './factory-function';

class Persona implements CartComparator<Persona> {
  id?: number;
  firstName: string | null;
  lastName: string | null;
  address?: Address;
  dob: Date | null;
  email: string | null;
  phoneNumber: string | null;
  gender?: string;
  graduationYear?: number;
  system?: string;
  type?: string;

  constructor(source?: {
    id?: number;
    firstName: string | null;
    lastName: string | null;
    address?: Address;
    dob: Date | null;
    email: string | null;
    phoneNumber: string | null;
    gender?: string;
    graduationYear?: number;
    system?: string;
    type?: string;
  }) {
    if (!source) return;
    this.firstName = source.firstName;
    this.lastName = source.lastName;
    this.address = new Address(source.address);
    this.dob = source.dob;
    this.email = source.email;
    this.phoneNumber = source.phoneNumber;
    this.gender = source.gender;
    this.graduationYear = source.graduationYear;
    this.id = source.id;
    this.system = source.system;
    this.type = source.type;
  }

  /**
   * Determines equality for determining how and whether to add an item to the cart.
   * @param a the Persona being compared
   * @param b this
   */
  equals(a: Persona, b: Persona = this): boolean {
    let result = a === b;
    if (!result && !!a) {
      result =
        a.firstName === b.firstName &&
        a.lastName === b.lastName &&
        !!a.address?.equals(b.address as Address) &&
        DateService.datesAreEqual(a.dob, b.dob) &&
        a.email === b.email &&
        a.phoneNumber === b.phoneNumber &&
        a.gender === b.gender &&
        a.graduationYear === b.graduationYear &&
        a.id === b.id;
    }
    return result;
  }
}

const registrantDtoToPersona: FactoryFn<RegistrantDto, Persona> = (
  source: RegistrantDto,
) => {
  const dob = isString(source.dob)
    ? DateService.fromIso(source.dob)
    : source.dob;
  return new Persona({
    id: source.id ?? undefined,
    firstName: source.firstName,
    lastName: source.lastName,
    address: source.address ? addressDtoToAddress(source.address) : undefined,
    dob: dob,
    email: source.email,
    phoneNumber: source.phoneNumber,
    system: source.system ?? undefined,
    type: source.type ?? undefined,
  });
};

const participantDtoToPersona: FactoryFn<ParticipantDto, Persona> = (
  source: ParticipantDto,
) => {
  const result = registrantDtoToPersona(source);
  result.gender = source.gender ?? undefined;
  result.graduationYear = source.graduationYear ?? undefined;

  return result;
};

const personaToRegistrantDto: FactoryFn<Persona, RegistrantDto> = (
  src: Persona,
) =>
  registrantFactory({
    firstName: src.firstName,
    lastName: src.lastName,
    phoneNumber: src.phoneNumber,
    address: src.address ? addressFactory(src.address) : null,
    dob: src.dob,
    email: src.email,
    id: src.id,
    system: src.system,
    type: src.type,
  });

const personaToParticipantDto: FactoryFn<Persona, ParticipantDto> = (
  src: Persona,
) => {
  return participantFactory({
    firstName: src.firstName,
    lastName: src.lastName,
    dob: src.dob,
    email: src.email,
    phoneNumber: src.phoneNumber,
    address: src.address ? addressFactory(src.address) : null,
    gender: src.gender,
    graduationYear: src.graduationYear ? Number(src.graduationYear) : null,
    id: src.id,
    system: src.system,
    type: src.type,
  });
};

export {
  Persona,
  participantDtoToPersona,
  personaToParticipantDto,
  personaToRegistrantDto,
  registrantDtoToPersona,
};
